/**
 * E-gift card redemption program — the single source of truth for the
 * merchant catalog and redemption rules.
 *
 * Imported by BOTH sides (same pattern as tiers.js):
 *   - src/pages/Rewards.jsx + the redeem modal render the catalog and
 *     pre-validate in the UI;
 *   - server/gift-card-redemptions.js enforces the same rules
 *     authoritatively — the client copy is a convenience, never the gate.
 *
 * Keep this file pure data + pure functions: no React, no icons, no env.
 */

/** 100 miles = $1 — the program-wide redemption rate. */
export const MILES_PER_DOLLAR = 100;

/** Smallest redeemable amount: 500 miles ($5). */
export const GIFT_CARD_MIN_MILES = 500;

/** Redemptions step in 500-mile ($5) increments. */
export const GIFT_CARD_DENOMINATION_MILES = 500;

/**
 * The 200+ participating merchants shown on the Rewards page.
 * Names are the fulfillment-catalog display names — don't "clean them up";
 * the team orders cards against these exact strings.
 */
export const GIFT_CARD_MERCHANTS = [
  '1800Flowers US', 'Abercrombie & Fitch', 'Academy Sports & Outdoors US', 'Ace Hardware', 'adidas US', 'Aerie - US', 'Airbnb', 'Albertson\'s/Safeway US', 'Albertsons - Alcohol/Tobacco Prohibited', 'Allbirds', 'AMC Theatres', 'Amazon.com', 'American Eagle US', 'Amtrak', 'Apple', 'Applebee\'s', 'Aquarium US', 'ASOS', 'Athleta',
  'Babin\'s Seafood House US', 'Bahama Breeze', 'Baja Fresh US', 'Banana Republic', 'Barnes & Noble', 'Baskin Robbins US', 'Bass Pro Shops US', 'Bath & Body Works', 'Bed Bath & Beyond', 'Belk', 'Bertucci\'s - US', 'Best Buy USA', 'BJ\'s Restaurant & Brewhouse - US', 'Blaze Pizza - US', 'Bloomin Brands', 'Bloomingdale\'s', 'Bob Evans Restaurants US', 'Bonefish Grill US', 'Brenner\'s Steakhouse US', 'Bubba Gump Restaurant', 'Buffalo Wild Wings USA', 'Burger King', 'Burlington',
  'Cabela\'s', 'California Pizza Kitchen', 'Callaway Golf - US', 'Carter\'s - US', 'CB2', 'Chart House Restaurant', 'Cheddar\'s Scratch Kitchen', 'Chevron and Texaco US', 'Chewy', 'Chili\'s', 'Chipotle', 'Coffee Bean & Tea Leaf', 'Cold Stone Creamery', 'Columbia Sportswear', 'Conoco', 'Cost Plus World Market US', 'Cracker Barrel', 'Crate & Barrel', 'CVS Pharmacy',
  'Darden Restaurants', 'Dave & Buster\'s', 'Delta Air Lines', 'Dick\'s Sporting Goods - US', 'Disney', 'Domino\'s', 'DoorDash', 'DSW Everyday', 'Dunkin\' US',
  'El Pollo Loco', 'Embers Restaurant US', 'Etsy',
  'Factor US', 'Famous Dave\'s US', 'Famous Footwear US', 'Fanatics', 'Fandango US', 'Finish Line', 'Five Guys US', 'Fleming\'s Prime Steakhouse & Wine Bar', 'Foot Locker US',
  'GameStop', 'Gandy Dancer US', 'Gap US', 'Giant Eagle US', 'Global Experiences Card US', 'Global Hotel Card US', 'GNC', 'Go Play Golf by Fairway Rewards US', 'Goodcents Deli Fresh Subs', 'Google Play', 'Grand Concourse US', 'Grotto US', 'GrubHub US',
  'H&M', 'Hard Rock Cafe', 'Harley-Davidson', 'Harris Teeter', 'Harry & David', 'Harvey\'s Supermarkets USA', 'Hello Fresh', 'Hibbett Sports US', 'Home Chef', 'HomeGoods', 'Hotels.com US', 'Hulu',
  'IHOP', 'IKEA', 'Instacart',
  'Jamba', 'JCPenney', 'Jersey Mike\'s - US', 'Jiffy Lube', 'Joe\'s Crab Shack',
  'Kendra Scott', 'KFC', 'Kohl\'s', 'Krispy Kreme', 'Kroger US',
  'L.L.Bean', 'Land\'s End - US', 'Landry\'s Restaurants', 'Landry\'s Seafood House', 'Longhorn Steakhouse', 'Lowe\'s', 'Lulus.com', 'Lyft US',
  'Macy\'s', 'Maggiano\'s Little Italy US', 'Marshalls', 'McAlister\'s Deli US', 'McCormick & Schmick\'s', 'Meijer USA', 'Michaels', 'Moe\'s Southwest Grill', 'Morton\'s The Steakhouse',
  'NFL Shop', 'Nike', 'Nordstrom', 'Nordstrom Rack US',
  'O\'Charley\'s - US', 'Old Navy US', 'Ole & Steen', 'Olive Garden', 'Omaha Steaks US', 'Oura Ring', 'Outback Steakhouse', 'Outdoorsy',
  'P.F. Chang\'s', 'Panda Express', 'Panera Bread', 'Papa John\'s', 'Paramount+', 'Paramount+ - US', 'Paul Martin\'s American Grill US', 'Peet\'s Coffee & Tea', 'Peohe\'s US', 'Petco US', 'PetSmart', 'Phillips 66 - Conoco - 76', 'Pilot Flying J', 'Pottery Barn', 'Publix', 'Puckett\'s Grocery US',
  'Qdoba', 'Quizno\'s US',
  'Rainforest Cafe Restaurant', 'Ray Ban US', 'Red Lobster', 'Red Robin', 'Regal Cinemas US', 'REI', 'Ruby Tuesday US', 'Rusty Pelican US', 'Ruth\'s Chris Steak House US',
  'Saks Fifth Avenue', 'Saltgrass Steak House Restaurant', 'Sam\'s Club', 'Schlotzsky\'s US', 'Sephora', 'Shake Shack USA', 'Sheetz US', 'Shell', 'Shoney\'s US', 'Shutterfly US', 'Sierra', 'Sonic USA', 'Southwest Airlines', 'Spa & Wellness by Spa Week', 'Sportsmans Warehouse US', 'Staples', 'Starbucks US', 'Steak \'n Shake US', 'StubHub', 'Subway', 'Sunglass Hut', 'Sur La Table',
  'T.J.Maxx', 'Taco Bell', 'Target', 'Texas Roadhouse', 'TGI Fridays', 'The Cheesecake Factory', 'The Children\'s Place US', 'The Home Depot', 'The Oceanaire Restaurant', 'The Vitamin Shoppe', 'Topgolf', 'Tractor Supply', 'Tripgift - AirlineGift', 'TripGift US', 'Turo US',
  'Uber', 'Uber Eats', 'Ulta', 'Under Armour US', 'VALORANT', 'Victoria\'s Secret', 'Victoria\'s Secret PINK', 'Virgin Experience',
  'Walgreens US', 'Walmart', 'Wawa', 'Wayfair', 'West Elm', 'Williams-Sonoma', 'Willie G\'s US', 'Winn Dixie USA',
  'Zappos', 'Zaxby\'s'
].sort((a, b) => a.localeCompare(b));

/** Fast membership check for server-side validation. */
const MERCHANT_SET = new Set(GIFT_CARD_MERCHANTS);

export function isGiftCardMerchant(name) {
  return typeof name === 'string' && MERCHANT_SET.has(name);
}

/** 500 ≤ miles, whole number, in 500-mile steps. */
export function isValidGiftCardMiles(miles) {
  return (
    Number.isInteger(miles) &&
    miles >= GIFT_CARD_MIN_MILES &&
    miles % GIFT_CARD_DENOMINATION_MILES === 0
  );
}

/** Dollar value of a miles amount at the program rate. */
export function giftCardValueUsd(miles) {
  return (Number(miles) || 0) / MILES_PER_DOLLAR;
}

/**
 * The ONE dollar formatter for gift-card values. The overlay's receipt and
 * the server-written inquiry text must show the customer the same figure —
 * both import this instead of formatting locally.
 */
export function formatGiftCardUsd(miles) {
  return giftCardValueUsd(miles).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Largest redeemable amount within a balance (floors to the denomination). */
export function maxRedeemableMiles(balance) {
  const b = Math.max(0, Math.floor(Number(balance) || 0));
  return Math.floor(b / GIFT_CARD_DENOMINATION_MILES) * GIFT_CARD_DENOMINATION_MILES;
}
